import {
  Box,
  Button,
  Spinner,
  TextArea,
  TextInput,
  Text,
  useToast,
  Tooltip,
  TooltipInteractiveWrapper,
  InfoIcon,
} from '@razorpay/blade/components';
import { perquisitesGeneralDetailsStepValidationSchema } from 'components/WizardViews/SalaryComponents/constants';
import { PerquisitesFormData } from 'components/WizardViews/SalaryComponents/types';
import {
  FieldGroup,
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
  StepHeader,
} from 'components/WizardViews/components';
import { useFormWithProvider } from 'forms';
import { FormEvent, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { routePaths } from 'components/Routes/data';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { AppError } from 'utils/AppError';
import api from 'api';
import { useMutation } from 'react-query';
import { FullScreenLoader } from 'components/ui/FullScreenLoader';

type GeneralDetailsStepProps = {
  initialValues?: PerquisitesFormData;
  isLoading?: boolean;
  isError?: boolean;
  onRefresh?: () => void;
};

const GeneralDetailsStep = ({
  initialValues,
  isLoading = false,
  isError = false,
  onRefresh,
}: GeneralDetailsStepProps) => {
  const history = useHistory();
  const { formData, validate, register, setFormData } = useFormWithProvider<PerquisitesFormData>();
  const isEditMode = Boolean(initialValues?.id);
  const toast = useToast();

  const editPerquisitesComponentMutation = useMutation({
    mutationFn: api.salaryComponents.mutations.editPerquisitesComponent,
    onError: (e: AppError) => {
      toast.show({
        content: 'There was an error in editing salary component.',
        color: 'negative',
      });
    },
    onSuccess(data) {
      toast.show({
        content: 'Salary component updated successfully!',
        color: 'positive',
      });
      history.push(routePaths.salaryComponents.perquisitesListView);
    },
  });

  useEffect(() => {
    if (initialValues) {
      setFormData(initialValues as PerquisitesFormData);
    }
  }, [initialValues]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (validate(perquisitesGeneralDetailsStepValidationSchema)) {
      if (isEditMode) {
        const payload = {
          name: formData.name || '',
          displayName: formData.displayName || '',
          description: formData.description || '',
          status: formData.status || 'active',
          showZero: formData.showZero || false,
          visibleToEmployee: formData.visibleToEmployee || true,
          ...(formData.id && { id: formData.id }),
        };
        editPerquisitesComponentMutation.mutate({
          components: [{ id: formData.id!, ...payload }],
        });
      } else {
        history.push(routePaths.salaryComponents.create.perquisites.review);
      }
    }
  };

  if (isError) {
    return (
      <Box
        height={'100%'}
        backgroundColor={'surface.background.gray.intense'}
        alignItems={'center'}
        justifyContent={'center'}
        display={'flex'}>
        <EmptyListView
          title="This page could not be loaded due to unknown error. "
          description=""
          isError={true}>
          <Box display={'flex'} flexDirection={'row'} gap={'spacing.4'} marginX={'auto'}>
            <Button
              size="medium"
              onClick={() => {
                onRefresh?.();
              }}>
              Retry
            </Button>
            <Button
              size="medium"
              onClick={() => {
                history.push(routePaths.salaryComponents.perquisitesListView);
              }}>
              Contact support
            </Button>
          </Box>
        </EmptyListView>
      </Box>
    );
  }

  return isLoading ? (
    <FullScreenLoader accessibilityLabel="loading perquisites components" />
  ) : (
    <FormWrapper onSubmit={handleSubmit}>
      <StepContentWrapper>
        <Box marginBottom="spacing.8">
          <StepHeader title="General" subtitle="Configuration" />
        </Box>

        <FieldGroup title="General details">
          <TextInput
            label="Component name"
            placeholder="Enter component name"
            {...register('name')}
            isRequired
            isDisabled={isEditMode}
          />

          <TextInput
            label="Display name in payslip & Salary Register"
            placeholder="Enter display name"
            helpText="Display will be used in employee payslip and salary register"
            {...register('displayName')}
            isRequired
            trailing={
              <Tooltip content="The name that will appear in employee's payslip and Salary Register">
                <TooltipInteractiveWrapper>
                  <InfoIcon color="surface.icon.gray.muted" />
                </TooltipInteractiveWrapper>
              </Tooltip>
            }
          />

          <TextArea
            label="Component description"
            placeholder="e.g. Component provided to help employees cover their housing or rental expenses."
            {...register('description')}
          />
        </FieldGroup>
      </StepContentWrapper>

      <StepActionsWrapper>
        <span />
        <Button type="submit" isDisabled={isLoading}>
          {isEditMode ? 'Confirm Changes' : 'Continue'}
        </Button>
      </StepActionsWrapper>
    </FormWrapper>
  );
};

export default GeneralDetailsStep;
